import React, { useState } from 'react';
import {
  Form,
  Input,
  Button,
  Radio,
  Space,
  Typography,
  theme,
  Grid,
  Row,
  Col,
  Spin,
  Card,
  message,
  Flex,
  Select,
} from 'antd';
import {
  EyeOutlined,
  CheckOutlined,
  SolutionOutlined,
  ArrowsAltOutlined,
  ReadOutlined,
  LikeOutlined,
  SmileOutlined,
  LeftOutlined,
  AudioMutedOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../../../store';
import { useAssistant } from '../../../../contexts/AssistantContext';
import { useAuthGuard } from '../../../../hooks/useAuthGuard';
import { AppBreadcrumb, useBreadcrumb } from '../../../breadcrumb';
import { ReactComponent as IconConcise } from '../../../../static/img/IconConcise.svg';
import { ReactComponent as IconStandard } from '../../../../static/img/IconStandard.svg';
import { ReactComponent as IconExtended } from '../../../../static/img/IconExtended.svg';
import { ReactComponent as BasicSilia } from '../../../../assets/img/Basic-silia.svg';
import { ReactComponent as BasicEca } from '../../../../assets/img/Basic-eca.svg';
import { assistantService } from '../../../assistant-management/services/assistantManagementService';
import { PERSONALITY_COLORS, DEFAULT_GUARDRAILS, GUARDRAIL_PRESETS, MODEL_OPTIONS, MODELS_WITHOUT_API_KEY, WHATSAPP_MODE_OPTIONS } from '../../constants';
import { useMyAssistantPersonality } from '../../hooks/useMyAssistant';
import { useTheme } from '../../../../hooks/useTheme';
import './MyAssistantPersonality.css';
import { Chatbot } from '../../../../Components/Chatbot/Chatbot';
import { AdvancedConfiguration } from './AdvancedConfiguration';
import { TopicsManager } from './TopicsManager';
import { AccountSelectionModal } from '../../../../Components/AccountSelectionModal/AccountSelectionModal';
import { AccountModal } from '../../../account/components/AccountModal/AccountModal';
import { accountService } from '../../../account/services/accountService';
import { useAuth } from '../../../auth';

import { AdvancedConfig, AppearanceState, Acronym } from '../../types';
import { toColorString, getAppearanceStateFromAssistant, transformAIResponseData } from '../../utils';
import { myAssistantServices } from '../../services/myAssistantServices';
import { v4 as uuidv4 } from 'uuid';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('features-assistants');

const { TextArea } = Input;
const { Title, Paragraph, Link } = Typography;
const { useBreakpoint } = Grid;

type PersonalityProps = {
  mode: 'edit' | 'create';
  onCreated?: (newAgentId: string) => void;
};

export default function MyAssistantPersonality({ mode, onCreated }: PersonalityProps): React.ReactElement {
  useAuthGuard(); // Proteger esta página con autenticación

  const { currentAccount } = useAccount();
  const { currentAssistant, refreshCurrentAssistant } = useAssistant();
  const { themeName } = useTheme();
  const { user, hasPermission } = useAuth();

  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isMobile = !screens.md;

  // Breadcrumb items
  const breadcrumbItems = useBreadcrumb({
    section: mode === 'edit' ? 'Personality' : 'Create Agent',
    includeAssistant: mode === 'edit'
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isAccountSelectionModalVisible, setIsAccountSelectionModalVisible] = useState(false);
  const [isCreateAccountModalVisible, setIsCreateAccountModalVisible] = useState(false);
  const [advancedConfig, setAdvancedConfig] = useState<AdvancedConfig>({
    guardrails: DEFAULT_GUARDRAILS,
    businessRules: [],
  });
  const [appearanceState, setAppearanceState] = useState<AppearanceState>(
    getAppearanceStateFromAssistant(currentAssistant)
  );
  const [personalityChatbotId, setPersonalityChatbotId] = useState<string | null>(null);
  const [personalityGeneratorConfig, setPersonalityGeneratorConfig] = useState<any>(null);
  const channelId = React.useMemo(() => uuidv4(), []);

  const watchedResponseLength = Form.useWatch('responseLength', form);
  const watchedCommunicationStyle = Form.useWatch('communicationStyle', form);
  const watchedVoiceLanguage = Form.useWatch('voiceLanguage', form);
  const watchedSelectedModel = Form.useWatch('selectedModel', form);

  const isApiKeyRequired = watchedSelectedModel && !MODELS_WITHOUT_API_KEY.includes(watchedSelectedModel);
  // Backend exposes only presence booleans; plaintext keys never reach the client.
  const hasStoredApiKey = Boolean(currentAssistant?.openAIConfig?.hasApiKey);
  const hasStoredProviderApiKey = Boolean(currentAssistant?.openAIConfig?.hasProviderApiKey);

  // Helper text for the per-model provider key, indexed by the model family prefix.
  const PROVIDER_KEY_HELPER: Record<string, string> = {
    claude: 'Anthropic API key (sk-ant-...). Used by LiteLLM to call Claude.',
    gemini: 'Google AI Studio API key. Used by LiteLLM to call Gemini.',
    bedrock: 'Not required — Bedrock uses IAM from the EKS node role.',
  };
  const providerKeyHelperText =
    PROVIDER_KEY_HELPER[(watchedSelectedModel || '').split('-')[0]] ||
    'OpenAI API key (sk-...). Used by LiteLLM to call the chosen OpenAI model.';

  useMyAssistantPersonality({ mode, currentAssistant, form, setAdvancedConfig });


  // Update appearance state when currentAssistant changes
  React.useEffect(() => {
    if (currentAssistant) {
      setAppearanceState(getAppearanceStateFromAssistant(currentAssistant));
    }
  }, [currentAssistant]);





  // Default cloud WebSocket URL
  const DEFAULT_WEBSOCKET_URL = process.env.REACT_APP_WS_SHARED_URL;

  // Initialize chatbot: get ID by flowType, then get config
  React.useEffect(() => {
    const initializeChatbot = async () => {
      try {
        // Get chatbot ID dynamically by flowType
        const dynamicChatbotId = await myAssistantServices.getChatbotIdByFlowType('personality');

        if (!dynamicChatbotId) {
          logger.error('[Personality Generator] No chatbot found for flowType "personality"');
          return;
        }

        // Clear conversation history on reload
        localStorage.removeItem(`chatbot_messages_${dynamicChatbotId}`);
        localStorage.removeItem(`chatbot_greeting_shown_${dynamicChatbotId}`);

        setPersonalityChatbotId(dynamicChatbotId);

        // Get chatbot configuration
        const responseData = await myAssistantServices.getChatbotConfig(dynamicChatbotId);
        const webchatConfig = responseData.webchatConfig;
        const wsUrlToUse = DEFAULT_WEBSOCKET_URL || webchatConfig.wsUrl || process.env.REACT_APP_WS_URL;

        setPersonalityGeneratorConfig({
          botName: responseData.name || webchatConfig.botName || 'Personality Generator',
          colorMain: webchatConfig.colorMain,
          colorTitle: webchatConfig.colorTitle,
          iconBgColor: webchatConfig.iconBgColor || '#ffffff',
          wsUrl: wsUrlToUse,
        });
      } catch (error) {
        logger.error('[Personality Generator] Error initializing chatbot:', error);
      }
    };

    initializeChatbot();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const values = form.getFieldsValue();
      logger.info('Saving with values:', values);
      logger.info('Advanced config:', advancedConfig);

      // Include advanced configuration in the save data
      const valuesWithAdvanced = {
        ...values,
        advancedConfig,
      };

      logger.info('Final values to save:', valuesWithAdvanced);

      await assistantService.handlePersonalitySave({
        values: valuesWithAdvanced,
        mode,
        currentAssistant,
        currentAccount,
        onCreated,
        navigate
      });

      // Refetch del assistant para que el contexto refleje los valores recién guardados.
      // Sin esto, salir y reentrar al tab muestra el estado previo al save (cache de React).
      if (mode === 'edit') {
        await refreshCurrentAssistant();
      }
    } catch (error) {
      logger.error('Error in handleSave:', error);
      // No necesitamos mostrar el mensaje aquí ya que se maneja en el servicio
    } finally {
      setIsSaving(false);
    }
  };

  const handleAIGenerate = async () => {
    try {
      const aiPrompt = form.getFieldValue('aiPrompt');
      setIsGenerating(true);
      await assistantService.handleGenerateAI({
        aiPrompt,
        mode,
        currentAssistant,
        form
      });
    } catch (error) {
      // Error is already handled in the service
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBackToAccountSelection = () => {
    setIsAccountSelectionModalVisible(true);
  };

  const handleAccountSelectionModalCancel = () => {
    setIsAccountSelectionModalVisible(false);
  };

  const handleAccountSelectionModalContinue = (selectedAccountId: string) => {
    setIsAccountSelectionModalVisible(false);
    navigate(`/accounts/${selectedAccountId}/assistants/new`);
  };

  const handleOpenCreateAccountModal = () => {
    setIsAccountSelectionModalVisible(false);
    setIsCreateAccountModalVisible(true);
  };

  const handleCreateAccountCancel = () => {
    setIsCreateAccountModalVisible(false);
  };

  const handleCreateAccountSubmit = async (values: any, modules: any[]) => {
    try {
      const billingPlanData = {
        monthlyBaseFee: parseFloat(values.monthlyBaseFee) || 0,
        conversationLimit: parseInt(values.includedConversations) || 0,
        extraConversationFee: parseFloat(values.extraConversationFee) || 0,
        effectiveFrom: values.effectiveFrom?.format('YYYY-MM') || values.effectiveFrom
      };

      const accountData = {
        email: values.contactEmail,
        fullName: values.companyName,
        companyName: values.companyName,
        companySizeId: undefined,
        phoneNumber: undefined,
        role: 'admin',
        status: 'active',
        type: values.accountType,
        billingPlan: billingPlanData,
      };

      const result = await accountService.createAccount(accountData);
      const resultAccountId = result.id;

      const enabledModules = modules.filter(m => m.isEnabled).map(m => m.id);
      const disabledModules = modules.filter(m => !m.isEnabled).map(m => m.id);

      if (enabledModules.length > 0) {
        const assignEnableModules = {
          moduleIds: enabledModules,
          isEnabled: true,
          modifiedBy: user?.id || '',
        };
        await accountService.assignModules(resultAccountId, assignEnableModules);
      }

      if (disabledModules.length > 0) {
        const assignDisableModules = {
          moduleIds: disabledModules,
          isEnabled: false,
          modifiedBy: user?.id || '',
        };
        await accountService.assignModules(resultAccountId, assignDisableModules);
      }

      message.success('Account created successfully');
      setIsCreateAccountModalVisible(false);
      setIsAccountSelectionModalVisible(true);

    } catch (error) {
      logger.error('Error creating account:', error);
      message.error('Failed to create account');
    }
  };

  const handleArtifactAccept = async (artifact: { content: string, language?: string }) => {
    try {
      // Parse the artifact content (expecting JSON format like AI response)
      const parsedData = JSON.parse(artifact.content);

      // Transform using existing utility (same as handleGenerateAI)
      const formValues = transformAIResponseData(JSON.stringify(parsedData));

      // Set form values
      form.setFieldsValue(formValues);

      // Handle advanced configuration if present
      if (parsedData.advanced_config) {
        let mergedGuardrails;

        // Check if using a preset
        if (parsedData.advanced_config.preset) {
          const presetName = parsedData.advanced_config.preset;
          const preset = GUARDRAIL_PRESETS.find(p => p.name === presetName || p.id === presetName);

          if (preset) {
            // Apply preset: enable guards in preset, disable others
            const presetGuardIds = new Set(preset.config.map(g => g.id));
            mergedGuardrails = DEFAULT_GUARDRAILS.map(defaultGuard => ({
              ...defaultGuard,
              enabled: presetGuardIds.has(defaultGuard.id),
            }));
          } else {
            // Preset not found, use defaults
            mergedGuardrails = DEFAULT_GUARDRAILS;
            message.warning(`Preset "${presetName}" not found, using defaults`);
          }
        } else {
          // Merge incoming guardrails with DEFAULT_GUARDRAILS
          // Keep all default guardrails, but enable/disable based on incoming config
          mergedGuardrails = DEFAULT_GUARDRAILS.map(defaultGuard => {
            const incomingGuard = parsedData.advanced_config.guardrails?.find(
              (g: any) => g.id === defaultGuard.id
            );

            // If incoming config has this guardrail, use its enabled state
            // Otherwise, keep it disabled (not included in artifact)
            return {
              ...defaultGuard,
              enabled: incomingGuard ? incomingGuard.enabled : false,
            };
          });
        }

        // Process business rules - add to local state only (will be saved when configuration is saved)
        let businessRulesResult: any[] = [];
        if (parsedData.advanced_config.business_rules?.length > 0) {
          // Create business rules without saving to API
          businessRulesResult = parsedData.advanced_config.business_rules.map((rule: string, index: number) => ({
            id: `ai-${Date.now()}-${index}`,
            rule: rule,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }));
        }

        const newAdvancedConfig: AdvancedConfig = {
          guardrails: mergedGuardrails,
          businessRules: businessRulesResult,
        };

        setAdvancedConfig(newAdvancedConfig);
      }

    } catch (error) {
      logger.error('Error processing artifact:', error);
      message.error('Invalid artifact format. Expected JSON configuration.');
    }
  };

  return (
    <>
      <AppBreadcrumb items={breadcrumbItems} />
      <Card>
        <div className="my-assistant-personality">
          <Form
            layout="vertical"
            form={form}
            preserve={false}
          >
          <Row gutter={[16, 16]}>
            <Card className="personality-card">
              <Col xs={24}>
                <Title level={4} className="personality-title">Describe Your Ideal Agent</Title>
                <Paragraph type="secondary" className="personality-description">
                  Type a short description of how you want your Virtual Agent to behave, and we'll
                  pre-fill the form for you.
                </Paragraph>
                <Form.Item name="aiPrompt">
                  {personalityGeneratorConfig && personalityChatbotId ? (
                    <Chatbot
                      wsUrl={personalityGeneratorConfig.wsUrl}
                      chatbotId={personalityChatbotId}
                      channelId={channelId}
                      urlApi={`${process.env.REACT_APP_API_HOST}/chatbot`}
                      isWeb={true}
                      isPreview={false}
                      primaryColor={personalityGeneratorConfig.colorTitle}
                      headerColor={personalityGeneratorConfig.colorMain}
                      iconColor={personalityGeneratorConfig.iconBgColor}
                      botName={personalityGeneratorConfig.botName}
                      onArtifactAccept={handleArtifactAccept}
                      autoAcceptArtifacts={false}
                    />
                  ) : (
                    <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
                      <Spin size="large" tip="Loading Personality Generator..." />
                    </Flex>
                  )}
                  {/* <TextArea
                  rows={4}
                  placeholder="Describe your ideal agent's behavior and personality..."
                  disabled={isGenerating}
                /> */}
                </Form.Item>
                {/* <Form.Item className="personality-form-item">
                <Space>
                  <Button
                    type="primary"
                    icon={isGenerating ? <Spin size="small" /> : themeName === 'silia' ? <BasicSilia width={16} height={16} /> : <BasicEca width={16} height={16} />}
                    onClick={handleAIGenerate}
                    disabled={isGenerating}
                    id="generate-personality-ai-button"
                  >
                    {isGenerating ? 'Generating...' : 'Generate with AI'}
                  </Button>
                </Space>
              </Form.Item> */}
              </Col>
            </Card>
            <Col xs={24}>
              <Title level={4} className="personality-title">Define Your Agent's Personality Manually</Title>
            </Col>
            <Col xs={24}>
              <Form.Item label="Agent name" name="agentName" tooltip="Provide a unique name for your Virtual Agent. This is how users will identify and refer to the agent during interactions.">
                <Input placeholder="Enter agent name" maxLength={50} showCount />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Business name" name="businessName" tooltip="Enter the name of your business that the agent will reference during conversations, reinforcing your brand.">
                <Input placeholder="Enter business name" maxLength={50} showCount />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="Communication style"
                name="communicationStyle"
                tooltip="Select the tone and style your Virtual Agent should use when communicating, such as friendly, formal, or professional."
              >
                <Radio.Group className="personality-radio-group">
                  <Space wrap size={isMobile ? 8 : 16}>
                    <Radio.Button
                      value="professional"
                      className={`personality-radio-button ${watchedCommunicationStyle === 'professional' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedCommunicationStyle === 'professional' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedCommunicationStyle === 'professional' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <SolutionOutlined className="personality-radio-button-icon" />
                      Professional
                    </Radio.Button>
                    <Radio.Button
                      value="neutral"
                      className={`personality-radio-button ${watchedCommunicationStyle === 'neutral' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedCommunicationStyle === 'neutral' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedCommunicationStyle === 'neutral' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <ArrowsAltOutlined className="personality-radio-button-icon" />
                      Neutral
                    </Radio.Button>
                    <Radio.Button
                      value="matter-of-fact"
                      className={`personality-radio-button ${watchedCommunicationStyle === 'matter-of-fact' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedCommunicationStyle === 'matter-of-fact' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedCommunicationStyle === 'matter-of-fact' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <ReadOutlined className="personality-radio-button-icon" />
                      Matter-of-fact
                    </Radio.Button>
                    <Radio.Button
                      value="friendly"
                      className={`personality-radio-button ${watchedCommunicationStyle === 'friendly' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedCommunicationStyle === 'friendly' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedCommunicationStyle === 'friendly' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <LikeOutlined className="personality-radio-button-icon" />
                      Friendly
                    </Radio.Button>
                    <Radio.Button
                      value="humorous"
                      className={`personality-radio-button ${watchedCommunicationStyle === 'humorous' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedCommunicationStyle === 'humorous' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedCommunicationStyle === 'humorous' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <SmileOutlined className="personality-radio-button-icon" />
                      Humorous
                    </Radio.Button>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label="Answer's Length"
                name="responseLength"
                tooltip="Define how long your agent’s answers should typically be. Choose between short, concise replies or more detailed explanations, depending on the user experience you want to offer."
              >
                <Radio.Group className="personality-radio-group">
                  <Space wrap size={isMobile ? 8 : 16}>
                    <Radio.Button
                      value="Concise"
                      className={`personality-radio-button ${watchedResponseLength === 'Concise' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedResponseLength === 'Concise' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedResponseLength === 'Concise' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <div className="personality-radio-button-content">
                        <IconConcise />
                        Concise
                      </div>
                    </Radio.Button>

                    <Radio.Button
                      value="Standard"
                      className={`personality-radio-button ${watchedResponseLength === 'Standard' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedResponseLength === 'Standard' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedResponseLength === 'Standard' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <div className="personality-radio-button-content">
                        <IconStandard />
                        Standard
                      </div>
                    </Radio.Button>

                    <Radio.Button
                      value="Extended"
                      className={`personality-radio-button ${watchedResponseLength === 'Extended' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedResponseLength === 'Extended' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedResponseLength === 'Extended' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <div className="personality-radio-button-content">
                        <IconExtended />
                        Extended
                      </div>
                    </Radio.Button>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="Voice Language"
                name="voiceLanguage"
                tooltip="Select the language for voice responses or turn off voice functionality. Your agent will use this language when providing audio responses to users."
              >
                <Radio.Group className="personality-radio-group">
                  <Space wrap size={isMobile ? 8 : 16}>
                    <Radio.Button
                      value="off"
                      className={`personality-radio-button ${watchedVoiceLanguage === 'off' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedVoiceLanguage === 'off' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedVoiceLanguage === 'off' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <div className="personality-radio-button-content">
                        <AudioMutedOutlined className="personality-radio-button-icon" />
                        Off
                      </div>
                    </Radio.Button>
                    <Radio.Button
                      value="spanish"
                      className={`personality-radio-button ${watchedVoiceLanguage === 'spanish' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedVoiceLanguage === 'spanish' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedVoiceLanguage === 'spanish' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <div className="personality-radio-button-content">
                        Spanish
                      </div>
                    </Radio.Button>
                    <Radio.Button
                      value="english"
                      className={`personality-radio-button ${watchedVoiceLanguage === 'english' ? 'selected' : ''}`}
                      style={{
                        borderColor: watchedVoiceLanguage === 'english' ? 'var(--ant-colorSecondary)' : undefined,
                        color: watchedVoiceLanguage === 'english' ? 'var(--ant-colorSecondary)' : undefined
                      }}
                    >
                      <div className="personality-radio-button-content">
                        English
                      </div>
                    </Radio.Button>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="WhatsApp Mode"
                name="whatsappMode"
                tooltip="Only for agents that operate on WhatsApp. Leave empty if this agent is used on web chat, mobile app, or other non-WhatsApp channels. 'Direct Messages' is for 1:1 conversations. 'WhatsApp Groups' enables group chat operation (e.g. monitoring, incident classification) and unlocks the WhatsApp Groups admin panel. 'Both' allows both modes."
              >
                <Select
                  placeholder="Select WhatsApp communication mode"
                  options={WHATSAPP_MODE_OPTIONS}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="Chat Topics"
                name="chatTopics"
                tooltip="Define the main topics that your assistant can discuss in conversations. These topics help guide the assistant's responses and expertise areas."
              >
                <TopicsManager disabled={isGenerating} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Domain topics" name="domainTopics" tooltip="List the key areas or topics your agent will specialize in, such as shipping, tracking, or returns. Separate topics with commas. This is used for the system prompt.">
                <Input placeholder="Enter domain topics for system prompt" maxLength={10000} showCount />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="Fallback response"
                name="fallbackResponse"
                tooltip="Define what your agent should say when it cannot answer a question. For example: 'I'm sorry, let me connect you to a human agent."
              >
                <Input placeholder="Enter fallback response" maxLength={200} showCount />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Role" name="role" tooltip="Describe the role your Virtual Agent will play in conversations. Specify whether it acts as a support assistant, sales representative, onboarding guide, or any other relevant role within your business context.">
                <Input placeholder="Enter agent role" maxLength={10000} showCount />
              </Form.Item>
            </Col>

            {/* OpenAI key — required for embeddings (chat history, semantic search, RAG). */}
            {/* Always OpenAI regardless of which completion model is chosen below. */}
            <Col xs={24}>
              <Form.Item
                label="OpenAI API Key (embeddings)"
                name="modelApiKey"
                tooltip="OpenAI API key used for embeddings (chat history in Pinecone, fallback detection, knowledge retrieval). Always required, regardless of the completion model you pick below."
                rules={mode === 'edit' && hasStoredApiKey
                  ? []
                  : [{ required: isApiKeyRequired, message: 'OpenAI API Key is required' }]}
                extra={mode === 'edit' && hasStoredApiKey ? 'Leave empty to keep the current OpenAI key.' : undefined}
              >
                <Input.Password
                  placeholder={mode === 'edit' ? 'Leave empty to keep current OpenAI key' : 'sk-...'}
                  visibilityToggle
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>

            {/* Completion model + its provider key — what answers the user. */}
            <Col xs={24}>
              <Form.Item
                label="Model"
                name="selectedModel"
                tooltip="Select the LLM model your agent will use to generate responses. Each model needs its provider API key in the field below."
              >
                <Select
                  placeholder="Select a model"
                  options={MODEL_OPTIONS}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="Model API Key"
                name="providerApiKey"
                tooltip="API key for the chosen completion model. Used by LiteLLM in place of the cluster default."
                extra={mode === 'edit' && hasStoredProviderApiKey ? 'Leave empty to keep the current Model API key.' : providerKeyHelperText}
              >
                <Input.Password
                  placeholder={mode === 'edit' ? 'Leave empty to keep current Model key' : 'Enter the API key for the selected model'}
                  visibilityToggle
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Advanced Configuration Section */}
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <AdvancedConfiguration
                value={advancedConfig}
                onChange={setAdvancedConfig}
                disabled={isGenerating}
                mode={mode}
              />
            </Col>
          </Row>

          <Form.Item className="personality-actions">
            <Space size={isMobile ? 8 : 16}>
              <Button
                type="primary"
                icon={isSaving ? <Spin size="small" /> : <CheckOutlined />}
                onClick={handleSave}
                disabled={isSaving}
                id="personality-save-button"
              >
                {isSaving ? (mode === 'edit' ? 'Saving...' : 'Creating Agent...') : (mode === 'edit' ? 'Save' : 'Create Agent')}
              </Button>
              {/*             <Button
              icon={<EyeOutlined />}
              onClick={() => setIsPreviewOpen(true)}
              disabled={isSaving}
              id="personality-preview-button"
            >
              Preview
            </Button> */}
            </Space>
          </Form.Item>
        </Form>

        {isPreviewOpen && (
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              zIndex: 1000,
            }}
          >
            <Chatbot
              wsUrl={currentAssistant?.websocketUrl || DEFAULT_WEBSOCKET_URL}
              chatbotId={currentAssistant?.id || '0'}
              channelId="personalityPreview"
              urlApi={`${process.env.REACT_APP_API_HOST}/chatbot`}
              isWeb={false}
              isPreview={true}
              primaryColor={toColorString(appearanceState.primaryColor)}
              headerColor={toColorString(appearanceState.headerColor)}
              iconColor={toColorString(appearanceState.iconColor)}
              botName={currentAssistant?.name}
            />
          </div>
        )}

        {/* Account Selection Modal */}
        <AccountSelectionModal
          visible={isAccountSelectionModalVisible}
          onCancel={handleAccountSelectionModalCancel}
          onContinue={handleAccountSelectionModalContinue}
          onCreateAccount={handleOpenCreateAccountModal}
          initialSelectedAccountId={currentAccount?.id}
        />

        {/* Create Account Modal */}
        <AccountModal
          isModalVisible={isCreateAccountModalVisible}
          handleCancel={handleCreateAccountCancel}
          handleSubmit={handleCreateAccountSubmit}
          isEditing={false}
        />
        </div>
      </Card>
    </>
  );
}
